import { useState } from "react";
import { Plus } from "lucide-react";
import { mutate } from "swr";
import { Button } from "~/components/ui/button";
import useToast from "~/components/ui/toast";
import { EntityListRow } from "~/pages/manifold-nextgen/components/EntityListRow";
import { TabHeader } from "~/pages/manifold-nextgen/components/TabHeader";
import { AgentAvatar } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { StatusBadge } from "~/pages/manifold-nextgen/components/agents/StatusBadge";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import { AgentStatus } from "~/services/agents";
import { projectAgentsPath, setProjectAgents } from "~/services/projects";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { useProjectContext } from "./useProjectContext";

export const ProjectAgents = () => {
  const { projectId, attachedAgents, isOwner } = useProjectContext();
  const { showSuccess } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);

  const { execute: removeAgent, isPending: isRemoving } = useAsyncMutation(
    (ids: string[]) => setProjectAgents(projectId, ids),
    {
      errorMessage: "Failed to remove agent",
      onSuccess: async () => {
        await mutate(projectAgentsPath(projectId));
        showSuccess("Agent removed");
      },
    }
  );

  const handleRemove = (agentId: string) => {
    const remaining = (attachedAgents ?? []).filter((a) => a.id !== agentId).map((a) => a.id);
    void removeAgent(remaining);
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto flex max-w-3xl flex-col gap-4">
        <TabHeader title="Agents" />
        <div className="flex items-center justify-end gap-3">
          {isOwner && (
            <Button
              variant="ghost"
              className="text-primary hover:text-primary/80"
              onClick={() => setDialogOpen(true)}
            >
              <Plus className="size-4" />
              Add Agents
            </Button>
          )}
        </div>

        {attachedAgents === undefined ? null : attachedAgents.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No agents attached.</p>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {attachedAgents.map((agent) => {
              const isDeleted = agent.status === AgentStatus.DELETED;
              return (
                <EntityListRow
                  key={agent.id}
                  href={isDeleted ? undefined : `${ROUTE_NEXTGEN_VIEW}/agents/${agent.id}`}
                  leading={<AgentAvatar id={agent.id} name={agent.name} size={32} />}
                  title={agent.display_name}
                  description={agent.description}
                  badge={
                    agent.status === AgentStatus.DISABLED || isDeleted ? (
                      <StatusBadge status={agent.status} />
                    ) : undefined
                  }
                  onRemove={isOwner ? () => handleRemove(agent.id) : undefined}
                  removeLabel={`Remove ${agent.display_name}`}
                  isRemoving={isRemoving}
                  className={isDeleted ? "opacity-50 grayscale" : undefined}
                />
              );
            })}
          </ul>
        )}

        <ProjectAgentsDialog
          projectId={projectId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          attached={attachedAgents ?? []}
        />
      </div>
    </div>
  );
};
